// ChatPage — 微信风格聊天界面
// 用户气泡: 绿色 #07c160 右对齐; 对方气泡: 浅色左对齐; 圆形头像; 时间胶囊

import { useState, useRef, useEffect, useMemo } from 'react';
import type { ChatMessage } from '@kuaichuan/shared';
import { useChat } from '../../hooks/useChat';
import { useAuthStore } from '../../store/authStore';

// 常用表情（Unicode emoji）
const EMOJIS = ['😀','😁','😂','🤣','😊','😍','🥰','😘','😎','🤔','😴','😭','😡','👍','👎','👏','🙏','💪','🎉','🔥','💯','❤️','💔','🌹','☕','🍔','🚀','⏰','📦','📱','💻','🎁'];

interface ChatPageProps {
  peerId: string | null;
  peerName: string;
  onBack?: () => void;
}

export function ChatPage({ peerId, peerName, onBack }: ChatPageProps) {
  const me = useAuthStore((s) => s.user);
  const { messages, connected, sending, send, loadMore, hasMore, peerTyping } = useChat(peerId);
  const [input, setInput] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 新消息自动滚到底部
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || !peerId) return;
    send('text', text);
    setInput('');
  };

  const handleEmoji = (emoji: string) => {
    setInput((prev) => prev + emoji);
  };

  const handleImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      alert('图片过大（>500KB），请通过文件传输功能发送');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      send('image', reader.result as string);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // 时间胶囊：每 5 分钟显示一次
  const timeMarkers = useMemo(() => {
    const markers = new Set<number>();
    let last = 0;
    for (const m of messages) {
      if (m.createdAt - last > 5 * 60 * 1000) {
        markers.add(m.createdAt);
        last = m.createdAt;
      }
    }
    return markers;
  }, [messages]);

  if (!peerId) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8">
        <div className="text-6xl mb-4">💬</div>
        <p className="text-lg font-medium text-ink">选择好友开始聊天</p>
        <p className="text-sm text-muted mt-2">在「好友」标签页选择一位好友，即可开始聊天</p>
        {onBack && (
          <button type="button" onClick={onBack} className="btn-primary mt-6">去好友列表</button>
        )}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-bg">
      {/* 顶部栏 */}
      <div className="flex items-center gap-3 px-4 py-3 bg-surface border-b border-border">
        {onBack && (
          <button type="button" onClick={onBack} className="text-muted hover:text-ink text-xl">‹</button>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-ink truncate">{peerName}</div>
          <div className={`text-xs ${connected ? 'text-success' : 'text-muted'}`}>
            {connected ? '在线' : '连接中…'}
          </div>
        </div>
      </div>

      {/* 消息列表 */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {hasMore && messages.length > 0 && (
          <div className="text-center mb-2">
            <button type="button" onClick={loadMore} className="text-xs text-primary">查看更多消息</button>
          </div>
        )}
        {messages.length === 0 && (
          <div className="text-center text-sm text-muted py-8">开始和 {peerName} 聊天吧</div>
        )}
        {messages.map((m) => {
          const isMe = m.senderId === me?.id;
          const showTime = timeMarkers.has(m.createdAt);
          return (
            <div key={m.id}>
              {showTime && <TimeCapsule ts={m.createdAt} />}
              <MessageBubble message={m} isMe={isMe} myName={me?.nickname ?? me?.email ?? ''} peerName={peerName} />
            </div>
          );
        })}
        {peerTyping && (
          <div className="flex items-center gap-2 px-2 py-1">
            <Avatar name={peerName} isMe={false} />
            <div className="px-3 py-2 rounded-2xl rounded-tl-sm bg-surface text-sm text-muted">正在输入…</div>
          </div>
        )}
      </div>

      {/* 表情选择器 */}
      {showEmoji && (
        <div className="grid grid-cols-8 gap-1 p-3 bg-surface border-t border-border max-h-48 overflow-y-auto">
          {EMOJIS.map((e) => (
            <button key={e} type="button" onClick={() => handleEmoji(e)} className="text-2xl p-1 hover:bg-bg rounded">
              {e}
            </button>
          ))}
        </div>
      )}

      {/* 底部输入栏 */}
      <div className="flex items-center gap-2 px-3 py-2 bg-surface border-t border-border" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 8px)' }}>
        <button type="button" onClick={() => setShowEmoji(!showEmoji)} className="text-2xl shrink-0 p-1">
          {showEmoji ? '⌨️' : '😊'}
        </button>
        <button type="button" onClick={() => fileInputRef.current?.click()} className="text-2xl shrink-0 p-1">🖼️</button>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImage} className="hidden" />
        <input
          className="input flex-1"
          placeholder="输入消息…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={sending || !input.trim()}
          className="px-4 py-2 rounded-full text-white text-sm font-medium shrink-0 transition-colors"
          style={{ backgroundColor: input.trim() ? '#07c160' : '#9ca3af' }}
        >
          发送
        </button>
      </div>
    </div>
  );
}

// 消息气泡
function MessageBubble({ message, isMe, myName, peerName }: { message: ChatMessage; isMe: boolean; myName: string; peerName: string }) {
  return (
    <div className={`flex items-start gap-2 px-1 py-1 ${isMe ? 'flex-row-reverse' : ''}`}>
      <Avatar name={isMe ? myName : peerName} isMe={isMe} />
      <div className={`max-w-[70%] ${isMe ? 'items-end' : 'items-start'} flex flex-col`}>
        {message.type === 'image' ? (
          <img src={message.content} alt="图片" className="max-w-full rounded-2xl max-h-60" />
        ) : message.type === 'emoji' ? (
          <div className="text-4xl px-2 py-1">{message.content}</div>
        ) : message.type === 'file-share' ? (
          <FileShareBubble content={message.content} isMe={isMe} />
        ) : (
          <div
            className={`px-3 py-2 rounded-2xl text-sm break-words whitespace-pre-wrap ${
              isMe
                ? 'rounded-tr-sm text-white'
                : 'rounded-tl-sm bg-surface text-ink'
            }`}
            style={isMe ? { backgroundColor: '#07c160' } : undefined}
          >
            {message.content}
          </div>
        )}
      </div>
    </div>
  );
}

// 文件分享气泡
function FileShareBubble({ content, isMe }: { content: string; isMe: boolean }) {
  try {
    const payload = JSON.parse(content) as { name: string; size: number; code: string; roomId: string };
    const sizeStr = payload.size > 1024 * 1024
      ? `${(payload.size / 1024 / 1024).toFixed(1)} MB`
      : `${(payload.size / 1024).toFixed(1)} KB`;
    return (
      <div className={`px-3 py-2 rounded-2xl ${isMe ? 'rounded-tr-sm' : 'rounded-tl-sm bg-surface'}`}
        style={isMe ? { backgroundColor: '#07c160', color: 'white' } : undefined}>
        <div className="flex items-center gap-2">
          <span className="text-2xl">📦</span>
          <div>
            <div className="text-sm font-medium">{payload.name}</div>
            <div className={`text-xs ${isMe ? 'opacity-80' : 'text-muted'}`}>{sizeStr}</div>
          </div>
        </div>
        <a href={`#join=${payload.code}`} className={`text-xs mt-1 inline-block ${isMe ? 'opacity-90' : 'text-primary'}`}>
          点击接收文件 →
        </a>
      </div>
    );
  } catch {
    return <div className="px-3 py-2 rounded-2xl bg-surface text-sm">📦 文件分享</div>;
  }
}

// 头像
function Avatar({ name, isMe }: { name: string; isMe: boolean }) {
  const initial = (name || '?')[0].toUpperCase();
  return (
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0"
      style={{ background: isMe ? 'linear-gradient(135deg,#07c160,#0ea5a4)' : 'linear-gradient(135deg,#6b7280,#9ca3af)' }}
    >
      {initial}
    </div>
  );
}

// 时间胶囊
function TimeCapsule({ ts }: { ts: number }) {
  const d = new Date(ts);
  const h = d.getHours().toString().padStart(2, '0');
  const m = d.getMinutes().toString().padStart(2, '0');
  return (
    <div className="flex justify-center my-2">
      <span className="text-xs text-muted bg-black/5 rounded-full px-2 py-0.5">{h}:{m}</span>
    </div>
  );
}
